/**
 * Phase 16 Plan 16-03 — ReopenWorkOrderDialog
 *
 * Simple confirm dialog for reopening a Completed Work Order (Completed → Active).
 */

import type { JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReopenWorkOrderMutation } from '@/hooks/useWorkOrderQueries';
import { toast } from '@/lib/toast';
import type { WorkOrderDetail } from '@/types/work-orders';

export interface ReopenWorkOrderDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (o: boolean) => void;
  readonly workOrder: WorkOrderDetail;
}

export function ReopenWorkOrderDialog({
  open,
  onOpenChange,
  workOrder,
}: ReopenWorkOrderDialogProps): JSX.Element {
  const mutation = useReopenWorkOrderMutation();

  function handleClose(): void {
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    mutation.mutate(workOrder.id, {
      onSuccess: () => {
        toast.success(`Work Order ${workOrder.woNumber} reopened`);
        onOpenChange(false);
      },
      onError: (err) => {
        toast.error('Reopen failed', { description: err.detail ?? err.message });
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="reopen-wo-dialog">
        <DialogHeader>
          <DialogTitle>Reopen Work Order</DialogTitle>
          <DialogDescription>
            Reopen <strong>{workOrder.woNumber}</strong> — this will move it back to Active status
            and clear the completion date.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="reopen-wo-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            data-testid="reopen-wo-dialog-confirm"
          >
            {mutation.isPending ? 'Reopening…' : 'Reopen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
